import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DataTable, DataTableRow, Pagination, TABLE_MAXH_TABS } from "@/components/molecules";
import { useOperator, usePagination, useSorting } from "@/hooks";
import { Vehicle } from "@/redux/slices/vehicle/types";
import { Page } from "@/redux/types";
import { VehicleTypesModal } from "../common/VehicleTypesModal";
import { myVehiclesColumns } from "./fixtures";
import { generateMyVehicleTableData } from "./helpers";
import { MyVehicleTableData } from "./types";

export interface MyVehiclesTableProps {
  items: Vehicle[];
  setPendingArchive: (vehicle: Vehicle) => void;
}

export const MyVehiclesTable = ({ items, setPendingArchive }: MyVehiclesTableProps) => {
  const { id: operatorId } = useOperator();
  const { sorting, setSorting } = useSorting();
  const [typesVehicle, setTypesVehicle] = useState<Vehicle | null>(null);

  const onArchiveClick = useCallback(
    (vehicle: Vehicle) => {
      setPendingArchive(vehicle);
    },
    [setPendingArchive]
  );

  const onShowTypes = useCallback((vehicle: Vehicle) => setTypesVehicle(vehicle), []);

  // The vehicle just added at Review (DEV-1903): highlighted, so the operator
  // sees where it went.
  const [searchParams] = useSearchParams();
  const added = searchParams.get("added");
  const getRowProps = useCallback(
    (row: DataTableRow<MyVehicleTableData>) =>
      added && row.original.uuid === added ? { className: "!bg-primary-tint-light", "data-added": "true" } : {},
    [added]
  );

  const data = useMemo(
    () => generateMyVehicleTableData(operatorId, items, onArchiveClick, onShowTypes),
    [items, onArchiveClick, onShowTypes, operatorId]
  );

  return (
    <>
      <DataTable
        className="w-full [&_table]:table-auto [&_tr]:bg-white"
        maxHeight={TABLE_MAXH_TABS}
        data={data}
        columns={myVehiclesColumns}
        sorting={sorting}
        setSorting={setSorting}
        getRowProps={getRowProps}
      />
      <VehicleTypesModal vehicle={typesVehicle} onClose={() => setTypesVehicle(null)} />
    </>
  );
};

const MyVehiclesTablePagination = ({ page }: { page: Page }) => {
  const { current, size, total, count } = page;
  const { handlePageClick, handleSizeChange } = usePagination("my-vehicles", 30);

  return (
    <Pagination
      onPageClick={handlePageClick}
      onSizeChange={handleSizeChange}
      currentPage={current}
      pageSize={size}
      totalItems={total}
      pageCount={count}
    />
  );
};

MyVehiclesTable.Pagination = MyVehiclesTablePagination;
